"use client";

import React, { forwardRef } from "react";
import { colors, spacing, layout } from "@/ui/tokens";
import { Input } from "./Input";
import { UsageDisplay } from "./UsageDisplay";
import { AttachmentTray } from "./AttachmentTray";
import { useChatStore, selectStatus, selectUsage } from "@/lib/sola/store";
import type { UseAttachmentsReturn } from "@/lib/sola/attachments/useAttachments";

interface InputAreaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onStop: () => void;
  disabled?: boolean;
  placeholder?: string;
  attachments?: UseAttachmentsReturn;
}

const PureInputArea = forwardRef<HTMLTextAreaElement, InputAreaProps>(
  function PureInputArea(
    { value, onChange, onSubmit, onStop, disabled = false, placeholder, attachments },
    ref
  ) {
    const status = useChatStore(selectStatus);
    const usage = useChatStore(selectUsage);

    const isLoading = status === "streaming" || status === "submitted";

    return (
      <div
        style={{
          boxShadow: `0 -1px 3px rgba(27, 49, 148, 0.08)`,
          backgroundColor: colors.canvas.default,
          padding: spacing[5],
        }}
      >
        <div style={{ maxWidth: layout.chatColumnWidth, margin: "0 auto" }}>
          {attachments && (
            <AttachmentTray
              pending={attachments.pending}
              errors={attachments.errors}
              onRemove={attachments.remove}
              onDismissError={attachments.dismissError}
            />
          )}

          <Input
            ref={ref}
            value={value}
            onChange={onChange}
            onSubmit={onSubmit}
            disabled={disabled || isLoading}
            status={status}
            onStop={onStop}
            placeholder={
              placeholder ||
              (isLoading
                ? "SOLA is analyzing your request..."
                : "Ask SOLA about recruiting, candidates, or market insights...")
            }
            onAttach={attachments?.add}
            attachUploading={attachments?.isUploading}
            hasAttachments={!!attachments?.hasReady}
          />

          {/* Usage display */}
          <UsageDisplay usage={usage} />
        </div>
      </div>
    );
  }
);

export const InputArea = React.memo(PureInputArea);
